import { useEffect, useRef, useState } from 'react';
import { cn } from '@/utils/cn';
interface PopoverProps {
  trigger: React.ReactNode;
  children: React.ReactNode;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const Popover = ({ trigger, children, isOpen: controlledIsOpen, onOpenChange }: PopoverProps) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    const newState = !isOpen;
    if (onOpenChange) {
      onOpenChange(newState);
    } else {
      setInternalIsOpen(newState);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        if (onOpenChange) {
          onOpenChange(false);
        } else {
          setInternalIsOpen(false);
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onOpenChange]);

  return (
    <div className="relative">
      <div className="cursor-pointer" ref={triggerRef} onClick={handleToggle}>
        {trigger}
      </div>
      {isOpen && (
        <div
          ref={popoverRef}
          className={cn(
            'absolute right-0 bg-white rounded-xl shadow-lg border border-[#E5E5E5] overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200'
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
};

interface PopoverItemProps {
  children: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  variant?: 'default' | 'danger';
}

export const PopoverItem = ({ children, onClick, variant = 'default' }: PopoverItemProps) => {
  return (
    <button
      onClick={onClick}
      className={`w-full px-4 py-3 text-left text-sm font-medium transition-colors first:rounded-t-xl last:rounded-b-xl ${
        variant === 'danger' ? 'text-danger bg-muted-light' : 'text-card-foreground'
      }`}
    >
      {children}
    </button>
  );
};
